import { Suspense } from 'react';

import { lazyRoute } from 'src/routes/lazy-route';
import { Navigate, Outlet } from 'react-router-dom';
import { ENVIRONMENT } from 'src/config-global';
import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';
// auth
import { AuthGuard } from 'src/auth/guard';
import AdminGuard from 'src/auth/guard/admin-guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';

import { CheckoutProvider } from 'src/sections/customer/checkout/context';
import OverlayDetailsPage from 'src/pages/admin/overlays/overlay-details';
import CreateOverlayPage from 'src/pages/admin/overlays/create-overlay';
import OverlayEditPage from 'src/pages/admin/overlays/edit-overlay';
import AdminOrAgentGuard from 'src/auth/guard/admin-agent-guard';
import OperationsDashboardPage from 'src/pages/admin/dashboard-operations/operations';
import TableTextListView from 'src/sections/admin/table-text/view/table-text-list-view';
import TableTextEditView from 'src/sections/admin/table-text/view/table-text-edit-form';
import TargetsPage from 'src/sections/admin/targets/targets-page';
import ControlsPage from 'src/pages/admin/controls';
import CatalogPdfPage from 'src/pages/admin/catalog-pdf';
import { paths } from '../paths';

// ----------------------------------------------------------------------

// dashboard pages
const OverlayPage = lazyRoute(() => import('src/pages/admin/overlays/overlay-list'));
const SituazionePage = lazyRoute(() => import('src/pages/admin/dashboard-operations/situazione'));
const DaIncassarePage = lazyRoute(
  () => import('src/pages/admin/dashboard-operations/da-incassare'),
);
const MagazzinoFermoPage = lazyRoute(
  () => import('src/pages/admin/dashboard-operations/magazzino-fermo'),
);
const SalesStatsPage = lazyRoute(() => import('src/pages/admin/statistics/sales-stats'));
const FinanzaPage = lazyRoute(() => import('src/pages/admin/statistics/finanza'));
const StockQualityViniPage = lazyRoute(() => import('src/pages/admin/stock-quality/vini'));
const StockQualityProduttoriPage = lazyRoute(
  () => import('src/pages/admin/stock-quality/produttori'),
);
const ClientPortfolioPage = lazyRoute(() => import('src/pages/admin/statistics/client-portfolio'));
const ProducerAnalysisPage = lazyRoute(
  () => import('src/pages/admin/statistics/producer-analysis'),
);
const ProductAnalysisPage = lazyRoute(() => import('src/pages/admin/statistics/product-analysis'));
const ClientAnalysisPage = lazyRoute(() => import('src/pages/admin/statistics/client-analysis'));
const AgentsAnalysisPage = lazyRoute(() => import('src/pages/admin/statistics/agents-analysis'));
const AssistantOverviewPage = lazyRoute(() => import('src/pages/admin/assistant/overview'));

const ClientOrdersPage = lazyRoute(() => import('src/pages/admin/client-order/list'));
const ClientOrderCreatePage = lazyRoute(() => import('src/pages/admin/client-order/new'));
const ClientOrderEditPage = lazyRoute(() => import('src/pages/admin/client-order/edit'));
const ClientOrderDetailsPage = lazyRoute(() => import('src/pages/admin/client-order/details'));
const ProducerOrderCreatePage = lazyRoute(() => import('src/pages/admin/producer-order/new'));
const ProducerOrderEditPage = lazyRoute(() => import('src/pages/admin/producer-order/edit'));
// products
const AllTheProductsPage = lazyRoute(() => import('src/pages/admin/product/list'));
const ReorderSheetPage = lazyRoute(() => import('src/pages/admin/reorder-sheet/list'));
const ArchiveHoldListPage = lazyRoute(() => import('src/pages/admin/archive-hold/list'));
const ProductCreatePage = lazyRoute(() => import('src/pages/admin/product/new'));
const ProductEditPage = lazyRoute(() => import('src/pages/admin/product/edit'));
const ProducersAdminPage = lazyRoute(() => import('src/pages/admin/producer/list'));
const ProducerCreatePage = lazyRoute(() => import('src/pages/admin/producer/new'));
const ProducerEditPage = lazyRoute(() => import('src/pages/admin/producer/edit'));
const ProductDetailsTabsPage = lazyRoute(() => import('src/pages/admin/product/details'));
const ProducerDetailsTabsPage = lazyRoute(() => import('src/pages/admin/producer/details'));
// user
const UserListPage = lazyRoute(() => import('src/pages/admin/user/list'));
const UserCreatePage = lazyRoute(() => import('src/pages/admin/user/new'));
const UserEditPage = lazyRoute(() => import('src/pages/admin/user/edit'));
const ClientListPage = lazyRoute(() => import('src/pages/admin/client/list'));
const ClientRetentionPage = lazyRoute(() => import('src/pages/admin/client/retention'));
const LeadInboxPage = lazyRoute(() => import('src/pages/admin/client/leads'));
const ScoutingPage = lazyRoute(() => import('src/pages/admin/client/scouting'));
const ClientCreatePage = lazyRoute(() => import('src/pages/admin/client/new'));
const ClientEditPage = lazyRoute(() => import('src/pages/admin/client/edit'));
const ClientDetailsPage = lazyRoute(() => import('src/pages/admin/client/details'));
const CompanyListPage = lazyRoute(() => import('src/pages/admin/company/list'));
const ProducerFicEntityListPage = lazyRoute(
  () => import('src/pages/admin/producer-fic-entity/list'),
);
const CompanyDetailsPage = lazyRoute(() => import('src/pages/admin/company/details'));
const ProducerCompanyListPage = lazyRoute(() => import('src/pages/admin/producer-company/list'));
const ProducerCompanyDetailsPage = lazyRoute(
  () => import('src/pages/admin/producer-company/details'),
);

const ReservationListPage = lazyRoute(() => import('src/pages/admin/reservation/list'));
const ReservationCreatePage = lazyRoute(() => import('src/pages/admin/reservation/new'));
const ReservationEditPage = lazyRoute(() => import('src/pages/admin/reservation/edit'));
const DiscountsListPage = lazyRoute(() => import('src/pages/admin/discounts/list'));
const ReconciliationListPage = lazyRoute(() => import('src/pages/admin/reconciliation/list'));
const TastingListPage = lazyRoute(() => import('src/pages/admin/tasting/list'));
const TastingCreatePage = lazyRoute(() => import('src/pages/admin/tasting/new'));
const TastingDetailsPage = lazyRoute(() => import('src/pages/admin/tasting/details'));
const TastingEditPage = lazyRoute(() => import('src/pages/admin/tasting/edit'));
const AgentsListPage = lazyRoute(() => import('src/pages/admin/agent/list'));
const AgentCreatePage = lazyRoute(() => import('src/pages/admin/agent/new'));
const AgentEditPage = lazyRoute(() => import('src/pages/admin/agent/edit'));
const AgentDetailsPage = lazyRoute(() => import('src/pages/admin/agent/details'));
const UserDetailsPage = lazyRoute(() => import('src/pages/admin/user/details'));
// statistics pages
const ProfitabiltyPage = lazyRoute(() => import('src/pages/admin/statistics/profitability'));
const ProfitabilityDetailsPage = lazyRoute(
  () => import('src/pages/admin/statistics/profitability-details'),
);
const WineTypeListPage = lazyRoute(() => import('src/pages/admin/wine-type/list'));
const WineTypeCreatePage = lazyRoute(() => import('src/pages/admin/wine-type/new'));
const WineTypeEditPage = lazyRoute(() => import('src/pages/admin/wine-type/edit'));
const WineTypeDetailsPage = lazyRoute(() => import('src/pages/admin/wine-type/details'));
const GrapeTypeListPage = lazyRoute(() => import('src/pages/admin/grape-types/list'));
const GrapeTypeDetailsPage = lazyRoute(() => import('src/pages/admin/grape-types/details'));
const GrapeTypeCreatePage = lazyRoute(() => import('src/pages/admin/grape-types/new'));
const GrapeTypeEditPage = lazyRoute(() => import('src/pages/admin/grape-types/edit'));
const TerritoryListPage = lazyRoute(() => import('src/pages/admin/territory/list'));
const TerritoryDetailsPage = lazyRoute(() => import('src/pages/admin/territory/details'));
const TerritoryCreatePage = lazyRoute(() => import('src/pages/admin/territory/new'));
const TerritoryEditPage = lazyRoute(() => import('src/pages/admin/territory/edit'));
const SelectionsListPage = lazyRoute(() => import('src/pages/admin/selections/list'));
const SelectionsCreatePage = lazyRoute(() => import('src/pages/admin/selections/new'));
const SelectionsEditPage = lazyRoute(() => import('src/pages/admin/selections/edit'));

// ----------------------------------------------------------------------

const isStagingOrLocalDev =
  ENVIRONMENT === CONSTANT_VARIABLES.STAGE ||
  ENVIRONMENT === CONSTANT_VARIABLES.DEVELOP ||
  ENVIRONMENT === CONSTANT_VARIABLES.PRODUCTION
    ? [
        {
          path: paths.admin.statisticTypes.root,
          children: [
            {
              path: paths.admin.statisticTypes.profitability,
              element: (
                <AdminGuard>
                  <ProfitabiltyPage />
                </AdminGuard>
              ),
              index: true,
            },
            {
              path: paths.admin.statisticTypes.profitability_details(':slug'),
              element: (
                <AdminGuard>
                  <ProfitabilityDetailsPage />
                </AdminGuard>
              ),
              index: true,
            },
          ],
        },
      ]
    : [];

export const adminRoutes = [
  {
    element: (
      <AuthGuard>
        <CheckoutProvider>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </CheckoutProvider>
      </AuthGuard>
    ),
    children: [
      {
        path: paths.admin.root,
        children: [
          {
            path: paths.admin.users.root,
            element: (
              <AdminGuard>
                <UserListPage />
              </AdminGuard>
            ),
            index: true,
          },
          {
            path: paths.admin.users.new,
            element: (
              <AdminGuard>
                <UserCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: `${paths.admin.users.root}/:slug`,
            element: (
              <AdminGuard>
                <UserDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.users.edit(':slug'),
            element: (
              <AdminGuard>
                <UserEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.clients.root,
            element: (
              <AdminOrAgentGuard>
                <ClientListPage />
              </AdminOrAgentGuard>
            ),
          },
          {
            path: paths.admin.clients.leads,
            element: (
              <AdminGuard>
                <LeadInboxPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2559 — admin only in 1a; agents arrive with NAT-2561.
            path: paths.admin.clients.scouting,
            element: (
              <AdminGuard>
                <ScoutingPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2523 — agents too: the backend scopes the list to their own book.
            path: paths.admin.clients.retention,
            element: (
              <AdminOrAgentGuard>
                <ClientRetentionPage />
              </AdminOrAgentGuard>
            ),
          },
          {
            path: `${paths.admin.clients.root}/:slug`,
            element: (
              <AdminGuard>
                <ClientDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.clients.new,
            element: (
              <AdminOrAgentGuard>
                <ClientCreatePage />
              </AdminOrAgentGuard>
            ),
          },
          {
            path: paths.admin.clients.edit(':slug'),
            element: (
              <AdminGuard>
                <ClientEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.companies.root,
            element: (
              <AdminGuard>
                <CompanyListPage />
              </AdminGuard>
            ),
          },
          {
            path: `${paths.admin.companies.root}/:companyId`,
            element: (
              <AdminGuard>
                <CompanyDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.producerCompanies.root,
            element: (
              <AdminGuard>
                <ProducerCompanyListPage />
              </AdminGuard>
            ),
          },
          {
            path: `${paths.admin.producerCompanies.root}/:ficEntityId`,
            element: (
              <AdminGuard>
                <ProducerCompanyDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.reservations.root,
            element: (
              <AdminGuard>
                <ReservationListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.discounts.root,
            element: (
              <AdminGuard>
                <DiscountsListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.reconciliation.root,
            element: (
              <AdminGuard>
                <ReconciliationListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.dashboard.overlays.root,
            element: (
              <AdminGuard>
                <OverlayPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.dashboard.table_text,
            element: (
              <AdminGuard>
                <TableTextListView />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.dashboard.assistant,
            element: (
              <AdminGuard>
                <AssistantOverviewPage />
              </AdminGuard>
            ),
          },
          {
            path: `${paths.admin.dashboard.table_text}/:key/edit`,
            element: (
              <AdminGuard>
                <TableTextEditView />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.dashboard.operations,
            element: (
              <AdminGuard>
                <OperationsDashboardPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2545 — margins and cost-derived figures, so admins only: agents must never
            // see cost (NAT-2530).
            path: paths.admin.dashboard.situazione,
            element: (
              <AdminGuard>
                <SituazionePage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2610 — receivables by client and the chase list. NAT-2795 (rule 52): open to
            // agents too, on their own clients only — the backend scopes by the live user and
            // leaves out the trend, Da sistemare and cost. The rest of Situazione stays admin.
            path: paths.admin.dashboard.daIncassare,
            element: (
              <AdminOrAgentGuard>
                <DaIncassarePage />
              </AdminOrAgentGuard>
            ),
          },
          {
            // NAT-2612 — stock at cost; admins only, like the Situazione page it opens from
            // (NAT-2530: agents never see these pages).
            path: paths.admin.dashboard.magazzinoFermo,
            element: (
              <AdminGuard>
                <MagazzinoFermoPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2579 — margins and cost in every response, so admins only; agents must never
            // see cost (NAT-2530). Their own view is NAT-2569.
            path: paths.admin.statisticTypes.salesStats,
            element: (
              <AdminGuard>
                <SalesStatsPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2811 — d36 on its own page; admins only, as the BI link it replaces was.
            path: paths.admin.statisticTypes.finanza,
            element: (
              <AdminGuard>
                <FinanzaPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2864 — cost and realisable value per wine: admins only (NAT-2530).
            path: paths.admin.stockQuality.vini,
            element: (
              <AdminGuard>
                <StockQualityViniPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.stockQuality.produttori,
            element: (
              <AdminGuard>
                <StockQualityProduttoriPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2749 — d26 on its own page: the same engine, so the same rule (NAT-2530).
            path: paths.admin.clientPortfolio,
            element: (
              <AdminGuard>
                <ClientPortfolioPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2760 — d15 and d17 on the same engine, so the same rule (NAT-2530).
            path: paths.admin.producerAnalysis,
            element: (
              <AdminGuard>
                <ProducerAnalysisPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.productAnalysis,
            element: (
              <AdminGuard>
                <ProductAnalysisPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2781 — d31 on the same engine, so the same rule (NAT-2530).
            path: paths.admin.clientAnalysis,
            element: (
              <AdminGuard>
                <ClientAnalysisPage />
              </AdminGuard>
            ),
          },
          {
            // The old Overview page was absorbed into Operativa (NAT-1714). The path stays
            // as a redirect so existing bookmarks keep working.
            path: paths.admin.dashboard.overview,
            element: <Navigate to={paths.admin.dashboard.operations} replace />,
          },
          {
            path: `${paths.admin.dashboard.overlays.root}/:id`,
            element: (
              <AdminGuard>
                <OverlayDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: `${paths.admin.dashboard.overlays.new}`,
            element: (
              <AdminGuard>
                <CreateOverlayPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.dashboard.overlays.edit(':id'),
            element: (
              <AdminGuard>
                <OverlayEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.reservations.new,
            element: (
              <AdminGuard>
                <ReservationCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.reservations.edit(':client_slug', ':sku'),
            element: (
              <AdminGuard>
                <ReservationEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.tastings.root,
            element: (
              <AdminGuard>
                <TastingListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.agents.root,
            element: (
              <AdminOrAgentGuard>
                <AgentsListPage />
              </AdminOrAgentGuard>
            ),
          },
          {
            path: paths.admin.agents.new,
            element: (
              <AdminGuard>
                <AgentCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.agents.edit(':slug'),
            element: (
              <AdminGuard>
                <AgentEditPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2749 — d32 on its own page. A static segment, so it ranks above `:slug`.
            path: paths.admin.agents.analysis,
            element: (
              <AdminGuard>
                <AgentsAnalysisPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.agents.details(':slug'),
            element: (
              <AdminGuard>
                <AgentDetailsPage />
              </AdminGuard>
            ),
          },
        ],
      },
      {
        path: paths.admin.root,
        children: [
          {
            path: paths.admin.orderTypes.clientOrders.root,
            element: (
              <AdminOrAgentGuard>
                <ClientOrdersPage />
              </AdminOrAgentGuard>
            ),
            index: true,
          },
          {
            path: paths.admin.orderTypes.clientOrders.new,
            element: (
              <AdminOrAgentGuard>
                <ClientOrderCreatePage />
              </AdminOrAgentGuard>
            ),
          },
          {
            path: paths.admin.orderTypes.clientOrders.edit(':order_code'),
            element: (
              <AdminOrAgentGuard>
                <ClientOrderEditPage />
              </AdminOrAgentGuard>
            ),
          },
          {
            path: paths.admin.orderTypes.clientOrders.details(':order_code'),
            element: (
              <AdminOrAgentGuard>
                <ClientOrderDetailsPage />
              </AdminOrAgentGuard>
            ),
          },
          {
            path: paths.admin.orderTypes.producerOrders.new,
            element: (
              <AdminGuard>
                <ProducerOrderCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.orderTypes.producerOrders.edit(':order_code'),
            element: (
              <AdminGuard>
                <ProducerOrderEditPage />
              </AdminGuard>
            ),
          },
        ],
      },
      {
        path: paths.admin.root,
        children: [
          {
            path: paths.admin.products.root,
            element: (
              <AdminGuard>
                <AllTheProductsPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2712 — «Da riordinare» was retired; «Foglio riordino» replaces it. The old path
            // stays as a redirect so existing bookmarks keep working.
            path: `${paths.admin.root}/reorder`,
            element: <Navigate to={paths.admin.reorderSheet.root} replace />,
          },
          {
            path: paths.admin.reorderSheet.root,
            element: (
              <AdminGuard>
                <ReorderSheetPage />
              </AdminGuard>
            ),
          },
          {
            // NAT-2654 — the archive carries cost; admins only (NAT-2530: agents never see cost).
            path: paths.admin.archiveHolds.root,
            element: (
              <AdminGuard>
                <ArchiveHoldListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.products.new,
            element: (
              <AdminGuard>
                <ProductCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.products.edit(':sku'),
            element: (
              <AdminGuard>
                <ProductEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.products.details(':sku'),
            element: (
              <AdminGuard>
                <ProductDetailsTabsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.producers.root,
            element: (
              <AdminGuard>
                <ProducersAdminPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.producerFicEntities.root,
            element: (
              <AdminGuard>
                <ProducerFicEntityListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.producers.new,
            element: (
              <AdminGuard>
                <ProducerCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.producers.edit(':slug'),
            element: (
              <AdminGuard>
                <ProducerEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.producers.details(':slug'),
            element: (
              <AdminGuard>
                <ProducerDetailsTabsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.wineTypes.root,
            element: (
              <AdminGuard>
                <WineTypeListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.wineTypes.new,
            element: (
              <AdminGuard>
                <WineTypeCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.wineTypes.edit(':id'),
            element: (
              <AdminGuard>
                <WineTypeEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.wineTypes.details(':id'),
            element: (
              <AdminGuard>
                <WineTypeDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.selections.root,
            element: (
              <AdminGuard>
                <SelectionsListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.selections.new,
            element: (
              <AdminGuard>
                <SelectionsCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.selections.edit(':id'),
            element: (
              <AdminGuard>
                <SelectionsEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.grapeTypes.root,
            element: (
              <AdminGuard>
                <GrapeTypeListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.grapeTypes.details(':id'),
            element: (
              <AdminGuard>
                <GrapeTypeDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.grapeTypes.new,
            element: (
              <AdminGuard>
                <GrapeTypeCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.grapeTypes.edit(':id'),
            element: (
              <AdminGuard>
                <GrapeTypeEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.tastings.root,
            element: (
              <AdminGuard>
                <TastingListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.territory.root,
            element: (
              <AdminGuard>
                <TerritoryListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.territory.new,
            element: (
              <AdminGuard>
                <TerritoryCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.territory.edit(':slug'),
            element: (
              <AdminGuard>
                <TerritoryEditPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.territory.details(':slug'),
            element: (
              <AdminGuard>
                <TerritoryDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.tastings.root,
            element: (
              <AdminGuard>
                <TastingListPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.tastings.new,
            element: (
              <AdminGuard>
                <TastingCreatePage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.tastings.edit(':slug'),
            element: (
              <AdminGuard>
                <TastingEditPage />
              </AdminGuard>
            ),
          },
          {
            path: `${paths.admin.tastings.root}/:slug`,
            element: (
              <AdminGuard>
                <TastingDetailsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.targets.root,
            element: (
              <AdminGuard>
                <TargetsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.controls.root,
            element: (
              <AdminGuard>
                <ControlsPage />
              </AdminGuard>
            ),
          },
          {
            path: paths.admin.catalogPdf.root,
            element: (
              <AdminGuard>
                <CatalogPdfPage />
              </AdminGuard>
            ),
          },
        ],
      },
      ...isStagingOrLocalDev,
    ],
  },
];
